import { ChangeDetectorRef, Component, OnDestroy, OnInit, HostBinding, HostListener, ViewEncapsulation, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';

import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreMediaService } from '@core/services/media.service';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';

import { AppPermissionsService, AuthenticationService } from '@common/services';

import { User } from '@common/models';
import { environment } from 'environments/environment';
import { MSGraphService } from '@core/services';
import { blobToBase64 } from '@core/utils';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { ChatDockService } from 'app/components/chat-ai/services/chat-dock.service';
import { UploadManagerService } from 'app/services/upload-manager.service';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class NavbarComponent implements OnInit, OnDestroy {
  public horizontalMenu: boolean;
  public hiddenMenu: boolean;

  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;

  public currentUser: User;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;

  public isDev: boolean = false;

  public profilePhoto: any;

  public canSeeBIX: boolean = false;

  @Input() navbarPinned: boolean = true;
  @Output() pinToggle = new EventEmitter<void>();

  @HostBinding('class.fixed-top')
  public isFixed = false;

  @HostBinding('class.navbar-static-style-on-scroll')
  public windowScrolled = false;

  @ViewChild('popoverBixNavbar')popoverBix: NgbPopover;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;

    // Static navbar style on scroll
    if (
      scrollTop > 100 &&
      this.coreConfig?.layout?.navbar?.type === 'navbar-static-top' &&
      this.coreConfig?.layout?.type === 'horizontal'
    ) {
      this.windowScrolled = true;
    } else if (scrollTop <= 10) {
      this.windowScrolled = false;
    }
  }

  toggleNavbarPin(): void {
    this.pinToggle.emit();
  }

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreMediaService} _coreMediaService
   * @param {TranslateService} _translateService
   */
  constructor(
    private _router: Router,
    private _permissionsService: AppPermissionsService,
    private _authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _coreMenuService: CoreMenuService,
    public _translateService: TranslateService,
    private _msGraphService: MSGraphService,
    private _cdr: ChangeDetectorRef,
    private _chatDockService: ChatDockService
  ) {

    this.isDev = !environment.production;

    this.languageOptions = {
      en: {
        title: 'English',
        flag: 'us'
      },
      es: {
        title: 'Español',
        flag: 'es'
      }
    };

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set the language
   *
   * @param language
   */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;

    // Use the selected language id for translations
    this._translateService.use(language);

    this._coreConfigService.setConfig({ app: { appLanguage: language } }, { emitEvent: true });
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // currentSkin is already kept up-to-date by the ngOnInit config subscription
    this.prevSkin = localStorage.getItem('prevSkin');

    if (this.currentSkin === 'dark') {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : 'default' } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem('prevSkin', this.currentSkin);
      this._coreConfigService.setConfig({ layout: { skin: 'dark' } }, { emitEvent: true });
    }
  }

  /**
   * Logout method
   */
  logout() {
    this._coreMenuService.unregister('main');
    this._authenticationService.logout();
    this._router.navigate(['/pages/authentication/login']);
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {

    // get the currentUser details from localStorage
    this._authenticationService.onUserValueChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(resp => {
      if (resp) {
        this.currentUser = resp;

        // Si la autenticación es por medio del active directory
        if(localStorage.getItem('auth-type') === 'saml'){
          // Registra el servicio para obtener el token
          this._msGraphService.register();
        }
      }
    });

    // Espera hasta obtener el token de acceso para msal graph
    this._msGraphService.onAccessTokenChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(resp => {
      if (resp){
        // Obtiene la foto de perfil
        this._msGraphService.getPhoto()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(resp => {
          blobToBase64(resp).then(value => {
            Promise.resolve().then(() => {
              this.profilePhoto = value;
              this._cdr.detectChanges();
            });
          });
        });
      }
    });

    // Evento que se dispara cuando se cambia el valor de los permisos
    this._permissionsService.onPermissionsChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe((res) => {
      if(res){
        Promise.resolve().then(() => {
          this.canSeeBIX = this._permissionsService.hasPermissionByUrl(`pages/chat`, "READ");
          this._cdr.detectChanges();
        });
      }
    });

    // Subscribe to the config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
      this.horizontalMenu = config.layout.type === 'horizontal';
      this.hiddenMenu = config.layout.menu.hidden === true;
      this.currentSkin = config.layout.skin;

      // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
      if (this.coreConfig.layout.type === 'vertical') {
        setTimeout(() => {
          if (this.coreConfig.layout.navbar.type === 'fixed-top') {
            this.isFixed = true;
          }
        }, 0);
      }
    });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == 'horizontal') {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        const isFixedTop = this._coreMediaService.currentMediaQuery === 'bs-xl';
        if (isFixedTop) {
          this.isFixed = false;
        } else {
          this.isFixed = true;
        }
      });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang
    });
  }

  ngAfterViewInit(): void {
    if (this.popoverBix){
      setTimeout(() => this.popoverBix.open(), 1000);
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Abre o cierra el panel acoplado de Bryant.
   *
   * Antes este botón navegaba a /pages/chat; ahora el chat se despliega al
   * costado y el contenido de la app se encoge para dejarle espacio.
   */
  toggleBryant(): void {
    this._chatDockService.toggle();
  }
}
